import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';

import countries from './modules/countries';
import featureFlags from './modules/feature-flags';
import featureGroups from './modules/feature-groups';
import languages from './modules/languages';
import learners from './modules/learners';
import manageLearnersReportDownloads from './modules/manageLearnersReportDownloads';
import now from './modules/now';
import organizations from './modules/organizations';
import permissions from './modules/permissions';
import features from './modules/features';
import pipeline from './modules/pipeline';
import reports from './modules/reports';
import timezones from './modules/timezones';
import scheduledActivations from './modules/settings/scheduled-activations';
// eslint-disable-next-line import/no-cycle
import user from './modules/user';
import courses from './modules/reports/courses';
import units from './modules/reports/units';
import groupClasses from './modules/reports/group-classes';
import privateClasses from './modules/reports/private-classes';
import vpaReport from './modules/reports/vpa';
import lessonHistory from './modules/reports/lesson-history';
import session from './modules/reports/session';
import k12SchoolEnrollment from './modules/reports/k12-school-enrollment';
import k12TeacherEnrollment from './modules/reports/k12-teacher-enrollment';
import schedule from './modules/schedule';
import teacherAdmin from './modules/teacher-admin';
import classes from './modules/classes';
import lessons from './modules/lessons';
import shareHistory from './modules/lessons/share-history';
import localSettings from './modules/localSettings';
import courseLanguages from './modules/courseLanguages';
import manageTeachers from './modules/manage-teachers';
import {
  LEARNERS_TABLE_KEY,
  MY_SCHEDULE_TABLE_KEY,
  ALL_CLASSES_TABLE_KEY,
  LESSONS_TABLE_KEY,
  SHARE_HISTORY_TABLE_KEY,
} from './table-persistence-keys';
import mergeColumns from './mergeColumns';
import learnerColumns from './modules/learners/columns';
import scheduleColumns from './modules/schedule/columns';

Vue.use(Vuex);

export const mutations = {
  SET_EXPORT_TYPE(state, { type }) {
    state.exportType = type;
  },
};

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    exportType: 'xlsx',
  },
  mutations,
  getters: {
    exportType: state => state.exportType,
  },
  modules: {
    countries,
    featureFlags,
    featureGroups,
    languages,
    learners,
    manageLearnersReportDownloads,
    now,
    organizations,
    permissions,
    features,
    pipeline,
    reports,
    timezones,
    user,
    courses,
    units,
    groupClasses,
    privateClasses,
    lessonHistory,
    session,
    schedule,
    teacherAdmin,
    classes,
    lessons,
    vpaReport,
    k12SchoolEnrollment,
    k12TeacherEnrollment,
    shareHistory,
    localSettings,
    scheduledActivations,
    courseLanguages,
    manageTeachers,
  },
  plugins: [
    createPersistedState({
      key: 'settings',
      paths: ['localSettings'],
    }),
    createPersistedState({
      key: LEARNERS_TABLE_KEY,
      paths: ['learners.columns', 'learners.creditsUnit', 'organizations.organizationId'],
      rehydrated: vuexStore => {
        const saved = vuexStore.state.learners.columns;
        vuexStore.commit('learners/SET_COLUMNS', mergeColumns(learnerColumns, saved));
      },
    }),
    createPersistedState({
      key: MY_SCHEDULE_TABLE_KEY,
      paths: ['schedule.columns'],
      rehydrated: vuexStore => {
        const saved = vuexStore.state.schedule.columns;
        vuexStore.commit('schedule/SET_COLUMNS', mergeColumns(scheduleColumns, saved));
      },
    }),
    createPersistedState({
      key: ALL_CLASSES_TABLE_KEY,
      paths: ['teacherAdmin.columns'],
    }),
    createPersistedState({
      key: LESSONS_TABLE_KEY,
      paths: ['lessons.columns'],
    }),
    createPersistedState({
      key: SHARE_HISTORY_TABLE_KEY,
      paths: ['shareHistory.columns'],
    }),
  ],
});

const initialStateCopy = JSON.parse(JSON.stringify(store.state));

export function resetState() {
  Object.assign(store.state, JSON.parse(JSON.stringify(initialStateCopy)));
}

export default store;
